import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { browserHistory, withRouter, RouteComponentProps } from 'react-router';

import Page from 'components/Pages/container';
import ErrorBoundary from 'components/ErrorBoundary';
import { getVenueId } from 'selectors/root';
import { shouldShowGuestDirectory } from 'selectors/features';
import { getBackButton } from 'selectors/browse';
import { getGuestDirectoryMenus, getGuestDirectorySectionsByMenuSlug } from 'selectors/guestDirectory';
import { useScrollPositions, usePrevious } from 'hooks';
import GuestDirectoryMenuCarousel from 'components/GuestDirectory/MenuCarousel';
import GuestDirectorySearch from 'components/GuestDirectory/Search';
import { setBackButton } from 'actions/browse';

import { TransitionContainer, Slide } from '../Browse/styles';

interface Props extends RouteComponentProps<{}, {}, { transition?: string }> {
  location: RouteComponentProps['location'];
  children?: React.ReactNode;
}

const GuestDirectory: React.FC<Props> = ({ location, children }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const venueId = useSelector(getVenueId);
  const featureEnabled = useSelector(shouldShowGuestDirectory);
  const backButton = useSelector(getBackButton);
  const menus = useSelector(getGuestDirectoryMenus);
  const cleanPathname = location.pathname.replace(/\//g, '');
  const [positionsObj] = useScrollPositions(cleanPathname);
  const [prevPathname] = usePrevious(cleanPathname);

  const menuSlug = location.pathname.match(/\/guest-directory\/menu\/([^/]+)/)?.[1];
  const sectionSlug = location.pathname.match(/\/section\/([^/]+)/)?.[1];
  const sections = useSelector(state => getGuestDirectorySectionsByMenuSlug(state, menuSlug || ''));

  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);

    if (pathSegments[0] !== 'guest-directory') {
      return;
    }

    let parentPath: string | null = null;

    if (pathSegments.length === 1) {
      parentPath = '/';
    } else if (pathSegments.length >= 3 && pathSegments[pathSegments.length - 2] === 'section') {
      parentPath = `/${pathSegments.slice(0, -2).join('/')}`;
    } else if (pathSegments.length >= 3 && pathSegments[pathSegments.length - 2] === 'menu') {
      parentPath = `/${pathSegments.slice(0, -2).join('/')}`;
    }

    if (parentPath) {
      dispatch(setBackButton('back', parentPath));
    }
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (!featureEnabled || !venueId) {
      browserHistory.replace('/');
    }
  }, [featureEnabled, venueId]);

  useEffect(() => {
    const { action, state } = location;

    if (action === 'POP' || state?.transition === 'Back') {
      const scrollPos = prevPathname ? positionsObj[prevPathname] : undefined;
      if (scrollPos) {
        window.scrollTo(0, scrollPos);
      }
    }
  }, [location, positionsObj, prevPathname]);

  if (!featureEnabled || !venueId) {
    return null;
  }

  const title = intl.formatMessage({ defaultMessage: 'Guest Directory' });

  return (
    <Page intlTitle={title} headerOnlyTitle={true} backButton={backButton}>
      <GuestDirectorySearch />
      {(menus.length > 0 || sections.length > 0) && (
        <GuestDirectoryMenuCarousel
          menuList={menus}
          sectionList={sections}
          menuSlug={menuSlug}
          sectionSlug={sectionSlug}
        />
      )}
      <ErrorBoundary errorMessage="Sorry, this guest directory could not be displayed. Please try again later.">
        <TransitionContainer>
          {/* @ts-expect-error - Slide styled component accepts direction prop */}
          <Slide direction={location?.state?.transition}>{children}</Slide>
        </TransitionContainer>
      </ErrorBoundary>
    </Page>
  );
};

export default withRouter(GuestDirectory);
